import React from 'react';
import { Link } from 'react-router-dom';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { Notification, getNotificationIcon } from '../../data/notificationsData';
import { CheckIcon, Trash2Icon } from 'lucide-react';
interface NotificationCardProps {
  notification: Notification;
  variant?: 'compact' | 'full';
  onMarkRead?: (id: string) => void;
  onAction?: (id: string, actionType: 'approve' | 'decline') => void;
  onRemove?: (id: string) => void;
}
export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  variant = 'full',
  onMarkRead,
  onAction,
  onRemove
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const Icon = getNotificationIcon(notification.type);
  // Determine icon color based on category and read status
  const getIconColor = () => {
    if (isAfterTide) return notification.read ? 'text-[#a0a0b0]' : 'text-[#e2b04a]';
    if (notification.read) return dk.textMuted;
    switch (notification.category) {
      case 'approvals':
        return rt.textPrimary;
      case 'orders':
        return 'text-blue-500';
      case 'releases':
        return 'text-green-500';
      case 'social':
        return 'text-pink-500';
      case 'events':
        return 'text-orange-500';
      case 'system':
        return 'text-gray-500';
      default:
        return rt.textPrimary;
    }
  };
  const getBgColor = () => {
    if (notification.read) {
      return variant === 'compact' ? dk.dropdownItemHover : 'hover:bg-opacity-50';
    }
    if (isAfterTide) {
      return 'bg-[#16213e]/80';
    }
    return dk.isDark ? 'bg-gray-800/80' : rt.bgLight;
  };
  const content = <div className="flex items-start w-full" data-id="element-3005" data-mp="15b7ag6gow6w">
      {/* Icon or Avatar */}
      <div className="flex-shrink-0 mr-4 mt-1" data-id="element-3006" data-mp="63cqr27suxiw">
        {notification.senderAvatar ? <img src={notification.senderAvatar} alt={notification.senderName} className="h-10 w-10 rounded-full object-cover" data-id="element-3007" data-mp="3oxtp89c809y" /> : <div className={`h-10 w-10 rounded-full flex items-center justify-center ${notification.read ? dk.surfaceMuted : rt.bgMedium}`} data-id="element-3008" data-mp="3qsvvmyu3w1dh">
            <Icon className={`h-5 w-5 ${getIconColor()}`} data-id="element-3009" data-mp="3lvvmug5vgkio" />
          </div>}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0" data-id="element-3010" data-mp="ne872flm294w">
        <div className="flex justify-between items-start" data-id="element-3011" data-mp="id2fil3osrwy">
          <p className={`text-sm font-semibold ${notification.read ? dk.textSecondary : dk.textPrimary}`} data-id="element-3012" data-mp="ifongluhp9fp">
            {notification.title}
          </p>
          <span className={`text-xs whitespace-nowrap ml-2 ${dk.textMuted}`} data-id="element-3013" data-mp="8n94pmbdcgtr">
            {notification.time}
          </span>
        </div>
        <p className={`mt-1 text-sm ${notification.read ? dk.textMuted : dk.textSecondary} ${variant === 'compact' ? 'line-clamp-2' : ''}`} data-id="element-3014" data-mp="11cgb17h2eapk">
          {notification.message}
        </p>

        {/* Related Image (Full variant only) */}
        {variant === 'full' && notification.relatedImage && <div className="mt-3" data-id="element-3015" data-mp="1il9428l3y7aj">
            <img src={notification.relatedImage} alt="Related content" className="h-16 w-12 object-cover rounded shadow-sm" data-id="element-3016" data-mp="1ik7hp7yn5ydi" />
          </div>}

        {/* Actions */}
        {notification.actionable && notification.actions && <div className="mt-3 flex flex-wrap gap-2" data-id="element-3017" data-mp="1wjpd466g09vu">
            <button onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onAction?.(notification.id, 'approve');
        }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${rt.primaryButton}`} data-id="element-3018" data-mp="21bgsrlo28v77">
              {notification.actions.approve || 'Approve'}
            </button>
            <button onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onAction?.(notification.id, 'decline');
        }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${rt.outlineButton}`} data-id="element-3019" data-mp="3d0e9qdsw0r3t">
              {notification.actions.decline || 'Decline'}
            </button>
          </div>}
      </div>

      {/* Quick Actions (Full variant only) */}
      {variant === 'full' && <div className="ml-4 flex items-center space-x-2 flex-shrink-0" data-id="element-3020" data-mp="2p3h2ay6ab7mf">
          {!notification.read && onMarkRead && <button onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onMarkRead(notification.id);
      }} className={`p-1.5 rounded-full transition-colors ${rt.textPrimary} hover:${rt.bgMedium}`} title="Mark as read" data-id="element-3021" data-mp="3341kbs4dkzev">
              <CheckIcon className="h-4 w-4" data-id="element-3022" data-mp="dfiubtbuxyp6" />
            </button>}
          {onRemove && <button onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onRemove(notification.id);
      }} className={`p-1.5 rounded-full transition-colors ${dk.textMuted} hover:${dk.textPrimary}`} title="Remove" data-id="element-3023" data-mp="pvs8zv9e2w4a">
              <Trash2Icon className="h-4 w-4" data-id="element-3024" data-mp="14kelzqbjnbsc" />
            </button>}
        </div>}
    </div>;
  const wrapperClasses = `block p-4 transition-colors ${getBgColor()} ${!notification.read && variant === 'full' && !isAfterTide ? 'border-l-4 ' + rt.borderActive : ''} ${variant === 'compact' ? 'border-b ' + dk.borderSubtle + ' last:border-0' : ''}`;
  if (notification.relatedLink) {
    return <Link to={notification.relatedLink} className={wrapperClasses} data-id="element-3025" data-mp="1niqvxeaa4poz">
        {content}
      </Link>;
  }
  return <div className={wrapperClasses} data-id="element-3026" data-mp="1sbtq9omvs368">{content}</div>;
};